/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {SensorTypes, VehicleWithTrailerTires, VehicleWithTrailerWheels} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '../theme/theme';
import {useStyles} from './BrakeChamberTrailerWidget';
import {SmartTrailerIcon} from './SmartTrailerIcon';

interface VehicleWithTrailerIconProps {
  width?: number
  height?: number;
  vehicleAxles?: number;
  trailerAxles?: number;
  issues?: VehicleWithTrailerTires[] | VehicleWithTrailerWheels[] ;
  onTireMouseOver?: (tire: VehicleWithTrailerTires) => void;
  onWheelMouseOver?: (tire: VehicleWithTrailerTires) => void;
  onTireClick?: (tire: VehicleWithTrailerTires) => void;
  onWheelClick?: (tire: VehicleWithTrailerWheels) => void;
  onTireMouseOut?: () => void;
  onWheelMouseOut?: () => void;
  className?: string;
  selectedItem?: VehicleWithTrailerTires | VehicleWithTrailerWheels;
  hoveredItem?: VehicleWithTrailerTires | VehicleWithTrailerWheels;
  anchorEl?: HTMLAnchorElement;
  sensorType?: SensorTypes;
}

const getSensorItemMapping =  () => {
  const colors = dlsAlt('colors', 'assets');
  return {
    tire : {
      light  : dlsColor('grey8'),
      normal : dlsColor('grey8'),
      dark   : dlsColor('paleBlue2')
    },
    wheel : {
      light  : dlsColor('grey8'),
      normal : dlsColor('grey8'),
      dark   : dlsColor('paleBlue2')
    },
    issue : {
      light  : colors.lightRed,
      normal : colors.normalRed,
      dark   : colors.darkRed
    }
  };
};

const sensorItemMapping = getSensorItemMapping();
export const getItemColor = ({
  type,
  item,
  issue,
  clickedItem
}) => {
  if (clickedItem) {
    if (clickedItem === item) {
      return issue ? sensorItemMapping.issue.dark : sensorItemMapping[type].dark;
    } else {
      return issue ? sensorItemMapping.issue.light : sensorItemMapping[type].light;
    }
  }
  return issue ? sensorItemMapping.issue.normal : sensorItemMapping[type].normal;
};

export const generateEvents = (onClick: Function, onMouserOver?: Function) => {
  if (!onClick) return null;
  return (val: VehicleWithTrailerTires | VehicleWithTrailerWheels) => function(e) {
    const event = {
      mouseover : onMouserOver,
      click     : onClick
    };
    event[e.type]?.(val);
  };
};

export const colorTransition = 'fill 500ms';

/**
 * Defines the Design Language System (DLS) VehicleWithTrailerIcon Icon component.
 *
 * Vehicle axles and trailer axles should be specified, although defaults are provided.
 * If no trailer axles then the trailer wont display. The vehicle should have at least 2 axles
 * (it wont display anything less than that *do you have a floating vehicle??) and the icon will show
 * at most 3 vehicle axles and 2 trailer axles.
 *
 * ```typescript
 * import {VehicleWithTrailerIcon, VehicleWithTrailerTires} from '@ion/dls/src/icons/';
 *
 * <VehicleWithTrailerIcon tireIssues={[
 *     VehicleWithTrailerTires.TrailerBackRightOutside,
 *     VehicleWithTrailerTires.VehicleFrontRight
 *   ]}
 *   wheelIssues={[
 *     VehicleWithTrailerWheels.HubRearRight
 *   ]}
 *   vehicleAxles={3}
 *   trailerAxles={2}
 *   onTireClick={onTireClick}
 *   onWheelClick={onWheelClick}
 *   selectedItem='trailerFrontLeft' />
 * ```
 */
export const VehicleWithTrailerIcon = ({
  height = 300,
  vehicleAxles = 3,
  trailerAxles = 0,
  issues = [],
  onTireClick,
  onWheelClick,
  onTireMouseOver,
  onWheelMouseOver,
  onTireMouseOut,
  onWheelMouseOut,
  className,
  selectedItem,
  hoveredItem,
  anchorEl,
  sensorType
}: VehicleWithTrailerIconProps) => {
  const classes                       = useStyles();
  const [clickedItem, setClickedItem] = useState(null);

  const hasTrailer = trailerAxles > 0;

  useEffect(() => {
    setClickedItem(selectedItem);
  }, [selectedItem]);

  function handleTireClick(tire: VehicleWithTrailerTires) {
    onTireClick?.(tire);
    setClickedItem(tire);
  }

  function handleWheelClick(wheel: VehicleWithTrailerWheels) {
    onWheelClick?.(wheel);
    setClickedItem(wheel);
  }

  return (
    <svg className={className}
      viewBox='0 0 300 140'
      height={'100%'}
      width={'100%'}
      fill='none'>
      {
        hasTrailer && (
          <SmartTrailerIcon trailerAxles={trailerAxles}
            issues={issues}
            onTireClick={handleTireClick}
            onWheelClick={handleWheelClick}
            onTireMouseOver={onTireMouseOver}
            onWheelMouseOver={onWheelMouseOver}
            onTireMouseOut={onTireMouseOut}
            onWheelMouseOut={onWheelMouseOut}
            hoveredItem={hoveredItem}
            clickedItem={clickedItem}
            sensorType={sensorType}
            anchorEl={anchorEl}/>
        )
      }
      <text x='205' y='14' className={classes.label}>{translate('common:smarterTrailer.brakeChamber.driverSide')}</text>
      <text x='185' y='118' className={classes.label}>{translate('common:smarterTrailer.brakeChamber.passengerSide')}</text>
    </svg>
  );
};
